import cn from 'classnames'
import React, { useCallback } from 'react'

import { testProps } from 'utils/test/qaData'

import type { IOption } from '../KitTypes'
import { KitSize } from '../KitTypes'
import { OptionLabel } from '../KitUtils'
import * as s from './Segmented.module.scss'

interface IProps<T> {
  item: IOption<T>
  name: string
  value?: T
  groupName: string
  onChange?: (value: T) => void
  size: KitSize
  resettable?: boolean
  disabled?: boolean
  onFocus?: () => void
  onBlur?: () => void
  minWidth?: number
}

const SegmentedElement = <T extends number | string | boolean>({
  item,
  value,
  onChange,
  onFocus,
  onBlur,
  size,
  groupName,
  name,
  disabled,
  resettable,
  minWidth = 54,
}: IProps<T>) => {
  const isActive = value === item.value

  const handleChange = useCallback(() => {
    if (!isActive) {
      onChange?.(item.value)
    } else if (resettable && isActive) {
      onChange?.(undefined as unknown as T)
    }
  }, [isActive, item.value, onChange, resettable])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLabelElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleChange()
      }
    },
    [handleChange],
  )

  return (
    <label
      {...testProps({ el: 'switcherItem', name, value: item.value, checked: isActive })}
      className={cn(s.label)}
      key={String(item.value)}
      onKeyDown={handleKeyDown}
      style={{ minWidth }}
      tabIndex={0}
    >
      <input
        checked={isActive}
        className={s.input}
        disabled={disabled}
        name={groupName}
        onBlur={onBlur}
        onClick={handleChange}
        onFocus={onFocus}
        type='radio'
        value={String(item.value)}
        readOnly
      />
      <span className={cn(s.item, s[size], { [s.active]: isActive, [s.disabled]: disabled })}>
        <OptionLabel option={item} size={KitSize.M} />
      </span>
    </label>
  )
}
export default SegmentedElement
